import React from 'react';
import type { DebugToggles } from '@/types/map';

interface MapDebugPanelProps {
  debugToggles: DebugToggles;
  setDebugToggles: React.Dispatch<React.SetStateAction<DebugToggles>>;
  showDebugPanel: boolean;
  setShowDebugPanel: React.Dispatch<React.SetStateAction<boolean>>;
  hospitalCount: number;
  entityCount: number;
}

export function MapDebugPanel({
  debugToggles,
  setDebugToggles,
  showDebugPanel,
  setShowDebugPanel,
  hospitalCount,
  entityCount,
}: MapDebugPanelProps) {
  if (!showDebugPanel) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[200000] bg-black/95 border border-yellow-500/60 rounded-lg shadow-2xl p-4 min-w-[340px] max-w-[400px] backdrop-blur-sm"
      style={{ fontFamily: 'monospace' }}
    >
      <div className="flex items-center justify-between mb-3 border-b border-yellow-500/30 pb-2">
        <span className="text-yellow-400 font-bold text-sm">DEBUG PANEL (Ctrl+D)</span>
        <button onClick={() => setShowDebugPanel(false)} className="text-gray-400 hover:text-white text-xs px-2 py-0.5 border border-gray-600 rounded">ESC</button>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        {([
          ['cesium3DTiles', '3D Tiles (Google)'],
          ['markers', 'Map Markers'],
          ['labels', 'Hospital Labels'],
          ['polylines', 'Marker Lines'],
          ['htmlOverlays', 'HTML Overlays'],
          ['skybox', 'Skybox'],
          ['borderImagery', 'Border Imagery'],
          ['animations', 'Render Loop'],
          ['rightPanel', 'Right Panel'],
          ['leftPanel', 'Left Panel'],
          ['topOverlays', 'Top HUD Panels'],
          ['watermark', 'Watermark Logo'],
          ['bottomCarousel', 'Bottom Carousel'],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-white/5 px-1 py-0.5 rounded">
            <input
              type="checkbox"
              checked={debugToggles[key]}
              onChange={() => setDebugToggles(prev => ({ ...prev, [key]: !prev[key] }))}
              className="accent-yellow-400 w-3.5 h-3.5"
            />
            <span className={debugToggles[key] ? 'text-green-400' : 'text-red-400'}>{label}</span>
          </label>
        ))}
      </div>
      <div className="mt-3 pt-2 border-t border-yellow-500/30 flex gap-2">
        <button
          onClick={() => setDebugToggles(prev => {
            const allOff = Object.values(prev).every(v => !v);
            const newState: any = {};
            for (const k of Object.keys(prev)) newState[k] = allOff;
            return newState;
          })}
          className="text-xs px-2 py-1 border border-gray-600 rounded text-gray-300 hover:text-white hover:border-gray-400"
        >
          Toggle All
        </button>
        <button
          onClick={() => {
            // Minimal mode: only Cesium + markers
            setDebugToggles({
              cesium3DTiles: true, markers: true, labels: false, polylines: false,
              htmlOverlays: false, rightPanel: false, leftPanel: false, topOverlays: false,
              watermark: false, bottomCarousel: false, animations: true, skybox: true, borderImagery: false,
            });
          }}
          className="text-xs px-2 py-1 border border-yellow-600 rounded text-yellow-300 hover:text-yellow-100 hover:border-yellow-400"
        >
          Minimal
        </button>
        <button
          onClick={() => {
            setDebugToggles({
              cesium3DTiles: true, markers: true, labels: true, polylines: true,
              htmlOverlays: false, rightPanel: true, leftPanel: true, topOverlays: true,
              watermark: true, bottomCarousel: true, animations: true, skybox: true, borderImagery: true,
            });
          }}
          className="text-xs px-2 py-1 border border-green-600 rounded text-green-300 hover:text-green-100 hover:border-green-400"
        >
          All On
        </button>
      </div>
      <div className="mt-2 text-[10px] text-gray-500">
        Hospitals loaded: {hospitalCount} | Entities: {entityCount}
      </div>
    </div>
  );
}

export default MapDebugPanel;
